import React, { useState, useCallback, useMemo } from 'react';
import { resolveIconUrl, parseIconId } from './registry';

export interface TechIconProps {
  /** 
   * Icon identifier in format "source:name" or just "name" (defaults to SimpleIcons)
   * @example "si:docker", "mdi:server-network", "carbon:bare-metal-server", "docker"
   */
  icon: string;
  /** Hex color without # (e.g., "2496ED"). Defaults to slate-400 */
  color?: string;
  /** Icon size in pixels. Defaults to 20 */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Alt text for accessibility. Defaults to empty string */
  alt?: string;
  /** Fallback icon if primary fails to load */
  fallback?: string;
  /** Callback when icon fails to load */
  onError?: () => void;
  /** Additional inline styles */
  style?: React.CSSProperties;
}

/**
 * Unified icon component that supports multiple icon sources
 * 
 * @example
 * // SimpleIcons (brands)
 * <TechIcon icon="si:docker" color="2496ED" />
 * <TechIcon icon="docker" color="2496ED" /> // shorthand, defaults to si:
 * 
 * // Material Design Icons (infrastructure)
 * <TechIcon icon="mdi:server-network" />
 * <TechIcon icon="mdi:nas" />
 * 
 * // Carbon Icons (cloud/enterprise)
 * <TechIcon icon="carbon:bare-metal-server" />
 * 
 * // Lucide Icons (UI)
 * <TechIcon icon="lucide:database" />
 * 
 * // Dashboard Icons (Homelab) - 1800+ pre-colored icons
 * <TechIcon icon="homelab:plex" />
 * <TechIcon icon="homelab:home-assistant" />
 * <TechIcon icon="homelab:proxmox" />
 */
export function TechIcon({
  icon,
  color = '94a3b8',
  size = 20,
  className = '',
  alt = '',
  fallback = 'si:codesandbox',
  onError,
  style,
}: TechIconProps) {
  const [hasError, setHasError] = useState(false);
  const [currentIcon, setCurrentIcon] = useState(icon);

  // Reset error state when icon prop changes
  React.useEffect(() => {
    setHasError(false);
    setCurrentIcon(icon);
  }, [icon]);

  // Memoize the URL to avoid recalculating on every render
  const iconUrl = useMemo(() => {
    const iconToResolve = hasError ? fallback : currentIcon;
    return resolveIconUrl(iconToResolve, color);
  }, [currentIcon, color, hasError, fallback]);

  const handleError = useCallback(() => {
    if (!hasError && fallback && currentIcon !== fallback) {
      setHasError(true);
      setCurrentIcon(fallback);
    }
    onError?.();
  }, [hasError, fallback, currentIcon, onError]);

  // Parse icon to get source for debugging
  const { source } = parseIconId(currentIcon);

  return (
    <img
      src={iconUrl}
      alt={alt}
      width={size}
      height={size}
      className={className}
      style={{
        objectFit: 'contain',
        ...style,
      }}
      onError={handleError}
      loading="lazy"
      decoding="async"
      data-icon-source={source}
      data-icon-id={currentIcon}
    />
  );
}

export default TechIcon;
